import { getLocale } from "./locale.js";
/**
 * Turning a full name into the fragments a screen shows.
 *
 * Shared because the reminder message is built from them and BOTH clients build
 * it — a reminder that greets "María" on the phone and "María Rodríguez" in the
 * browser is the same customer being addressed two different ways by the same
 * business.
 */
/** "María Rodríguez" → "María". Reminders greet a person, not a record. */
export function firstName(fullName) {
    return fullName.trim().split(/\s+/)[0] ?? fullName;
}
/** "María Rodríguez" → "MR", for an avatar circle. */
export function initials(fullName) {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? "";
    const second = parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : "";
    return (first + second).toUpperCase();
}
/** Digits only: what phone validation and WhatsApp both actually want. */
export function digitsOf(value) {
    return value.replaceAll(/\D/g, "");
}
/** How a local number is grouped on a card, by its length: 6000-0000, 300-123-4567. */
function groupSizesFor(length) {
    switch (length) {
        case 7:
            return [3, 4];
        case 8:
            return [4, 4];
        case 9:
            return [3, 3, 3];
        case 10:
            return [3, 3, 4];
        default:
            return [length];
    }
}
/**
 * The grouping a country's number actually takes: its own if the row carries
 * one, the rule by length otherwise.
 *
 * THE LENGTH RULE IS THE DEFAULT and the answer for nine of the ten
 * countries — one rule per length is one rule fewer to keep per country.
 * What it cannot do is disagree
 * with itself: eight digits are 6000-0000 in Panama and 99 123 456 in
 * Uruguay, so the country that disagrees says so on its own row rather than
 * the rule growing a country name inside it.
 *
 * The separator travels with the sizes for the same reason. The nine on the
 * length rule keep the dash they have always had, because they never read
 * this branch at all.
 */
function groupingFor(locale, length) {
    return locale.phoneGrouping ?? { sizes: groupSizesFor(length), separator: "-" };
}
/** The separator between the groups, applied to however many digits there are so far. */
function groupDigits(digits, grouping) {
    const parts = [];
    let at = 0;
    for (const size of grouping.sizes) {
        if (at >= digits.length)
            break;
        parts.push(digits.slice(at, at + size));
        at += size;
    }
    return parts.join(grouping.separator);
}
/** The country's own prefix dropped if it was typed in front of a full number; anything else left as it came. */
function withoutCountryCode(digits, locale) {
    const prefix = locale.phoneCountryCode;
    return digits.startsWith(prefix) && digits.length === locale.phoneDigits + prefix.length
        ? digits.slice(prefix.length)
        : digits;
}
/**
 * The trunk zero dropped: the 0 people dial in front of their own mobile
 * inside Venezuela, Ecuador and Uruguay, and write down the same way.
 *
 * Stated as ONE DIGIT TOO MANY AND THAT DIGIT A LEADING ZERO rather than as a
 * flag on those three rows, because that is the whole of it — a country whose
 * numbers have no spare zero can never satisfy it, so the other seven are
 * untouched (locale.test.ts holds each of them to the byte).
 *
 * Before this, the extra digit came off the END: 04121234567 in Venezuela was
 * stored as 0412123456 and the WhatsApp link, which is the country code plus
 * whatever is stored, opened 580412123456 — a chat with nobody.
 */
function withoutTrunkZero(local, locale) {
    return local.length === locale.phoneDigits + 1 && local.startsWith("0") ? local.slice(1) : local;
}
/** The digits of a local number: no country code in front, no trunk zero. */
function localDigits(value, locale) {
    return withoutTrunkZero(withoutCountryCode(digitsOf(value), locale), locale);
}
/**
 * "60000000" → "6000-0000", the way a number is written on a card in Panama;
 * "987654321" → "987-654-321" in Peru, "3001234567" → "300-123-4567" in
 * Colombia. Grouped by LENGTH rather than by a pattern per country: the
 * groups only have to be readable, and one rule per length is one rule fewer
 * to keep per country. A country whose own habit disagrees with the length --
 * Uruguay writes «99 123 456» where eight digits would come out «9912-3456»
 * -- carries its grouping on its row and overrides the rule for itself alone.
 * The country's own prefix is dropped if it was typed in front, and so is the
 * trunk zero. Anything of another length is left exactly as typed rather than
 * mangled into a shape it is not.
 */
export function formatPhone(value, locale = getLocale()) {
    const local = localDigits(value, locale);
    if (local.length !== locale.phoneDigits)
        return value;
    return groupDigits(local, groupingFor(locale, local.length));
}
/**
 * What a phone field shows WHILE SOMEBODY TYPES: the digits so far, in the
 * country's groups, and never one more digit than a number there has, so the
 * person sees the shape as it forms and cannot type
 * past it. Pasting a number with the country code in front loses the code:
 * «+57 300 123 4567» lands as «300-123-4567». Both clients' `PhoneInput`
 * run every keystroke through this.
 */
export function maskPhone(text, locale = getLocale()) {
    const local = localDigits(text, locale).slice(0, locale.phoneDigits);
    return groupDigits(local, groupingFor(locale, locale.phoneDigits));
}
/** The shape a number takes in that country, in zeros: «0000-0000», «000-000-0000», «00 000 000». */
export function phonePlaceholder(locale = getLocale()) {
    return maskPhone("0".repeat(locale.phoneDigits), locale);
}
/**
 * How wide the phone field is allowed to get, and it is the mask's width PLUS
 * ONE CHARACTER.
 *
 * Both clients' `PhoneInput` used the placeholder's own width, which is
 * exactly what a full number takes. That made the box refuse the keystroke
 * after the last one — and in Venezuela, Ecuador and Uruguay the keystroke
 * people type first is a trunk zero, so the number is one character longer on
 * the way in than it is at rest. The cap swallowed the eleventh digit before
 * `maskPhone` could drop the first, which is the bug the drop was meant to
 * fix.
 *
 * One, not two: the spare character is there for the zero the mask is about
 * to remove, not to let somebody type a longer number than the country has.
 * `maskPhone` still cuts at `phoneDigits`, so the extra room can only ever
 * hold a digit on its way to being dropped.
 */
export function phoneInputMaxLength(locale = getLocale()) {
    return phonePlaceholder(locale).length + 1;
}
